import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePurchaseStatus } from "../hooks/usePurchaseStatus";
import { SwarmField } from "../components/SwarmField";
import { MagicLinkCard } from "../components/MagicLinkCard";
import { MartzmakesWordmarkLink } from "../components/MartzmakesWordmark";
import {
  SwarmMoon,
  NSWordmark,
  Icon,
  I,
  StatusDot,
  StatusPill,
  MonoChip,
} from "../kit";
import "../marketing.css";

/**
 * Landing — the marketing page (phase 7, Track C). Translated from
 * design/project/Marketing.html + lib/mkt-top.jsx + lib/mkt-bot.jsx.
 * Layout/pixels follow the prototypes; the magic-link flow, signed-in
 * dashboard card, and legal routes are the production behavior that
 * must keep working. Phase 10 adds /pricing (nav + footer links), moves
 * MagicLinkCard to components/ so /pricing reuses it, and handles the
 * ?purchase=success|cancelled query Stripe checkout returns here with
 * (checkout.ts success_url/cancel_url point at SITE_URL/).
 */

/** Width-tracking hook — mirrors the prototype's NARROW context. Only
 * drives canvas density; everything visual is CSS in marketing.css. */
function useNarrow(query = "(max-width: 860px)") {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}

/* ── Nav ─────────────────────────────────────────────────────────── */

function MktNav() {
  const { status } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={"mkt-nav" + (scrolled ? " scrolled" : "")}>
      <div className="mkt-container mkt-nav-inner">
        <NSWordmark size={17} animate />
        <nav className="mkt-nav-links" aria-label="Sections">
          <a href="#how-it-works">How it works</a>
          <a href="#from-claude">From Claude</a>
          <a href="#dispatch">Dispatch profiles</a>
          <a href="#night-shift">Night shift</a>
          <a href="#capabilities">Capabilities</a>
          <a href="#for-teams">For teams</a>
          <Link to="/widgets">Widgets</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/docs">Docs</Link>
        </nav>
        <div className="mkt-nav-right">
          {status === "signed-in" ? (
            <Link to="/dashboard" className="ns-btn ns-btn--primary ns-btn--sm">
              Open dashboard
            </Link>
          ) : (
            <>
              <a className="mkt-nav-signin" href="#get-started">
                Sign in
              </a>
              <a className="ns-btn ns-btn--primary ns-btn--sm" href="#get-started">
                Get started
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Hero auth card (signed-in state; the magic-link card lives in
      components/MagicLinkCard so /pricing reuses it) ───────────────── */

function SignedInCard() {
  const { user } = useAuth();
  return (
    <div className="mkt-magic">
      <Icon d={I.user} size={15} style={{ color: "var(--color-fg-4)", flex: "none" }} />
      <span className="mkt-signedin-email">{user?.email}</span>
      <Link to="/dashboard" className="ns-btn ns-btn--primary ns-btn--sm">
        Open dashboard
      </Link>
    </div>
  );
}

/* ── Two-sided day ↔ night concept panel ─────────────────────────── */

function TwoSided() {
  return (
    <div className="mkt-two mkt-fade--panel">
      <div className="mkt-two-grid">
        <div className="mkt-two-day">
          <div className="mkt-panel-head">
            <Icon d={I.sun} size={16} />
            <span className="mkt-eyebrow">By day · you</span>
          </div>
          <div className="mkt-panel-title">You direct from the Claude app.</div>
          <div className="mkt-chat">
            <div className="mkt-bubble-user">
              Have research draft the Q3 competitive brief tonight.
            </div>
            <div className="mkt-bubble-bot">
              Scheduled for 02:00 on <span className="cmd">studio-mini</span>.
              I'll have it ready by sunrise.
              <div className="mkt-bubble-tools">
                <MonoChip tone="accent">
                  <Icon d={I.wrench} size={11} /> create_schedule
                </MonoChip>
              </div>
            </div>
          </div>
        </div>
        <div className="mkt-two-seam">
          <div className="mkt-dotgrid" />
          <span className="mkt-seam-moon">
            <SwarmMoon size={56} animate />
          </span>
          <span className="mkt-seam-arrow mkt-seam-arrow--r">
            <Icon d={I.arrowR} size={16} />
          </span>
          <span className="mkt-seam-arrow mkt-seam-arrow--d">
            <Icon d={I.arrowDown} size={16} />
          </span>
        </div>
        <div className="mkt-two-night">
          <div className="mkt-panel-head">
            <Icon d={I.moon} size={15} />
            <span className="mkt-eyebrow">By night · the swarm</span>
          </div>
          <div className="mkt-panel-title">The swarm runs while you sleep.</div>
          <div className="mkt-night-term">
            <div className="t-dim">02:00 · studio-mini</div>
            <div className="t-row">
              <span className="t-ok">›</span> research woke up{" "}
              <span className="t-dim">[claude]</span>
            </div>
            <div className="t-row">
              <span className="t-acc">⚙</span> web.search ×14 · read ×31
            </div>
            <div className="t-row">
              <span className="t-acc">⚙</span> draft.write brief.md
            </div>
            <div className="t-ok">✓ done · 6m12s · delivered</div>
            <div className="t-dim">03:10 · studio-mini</div>
            <div className="t-row">
              <span className="t-ok">›</span> refactor woke up{" "}
              <span className="t-dim">[codex]</span>
            </div>
            <div className="t-ok">✓ done · 4m38s · delivered</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Hero ────────────────────────────────────────────────────────── */

function MktHero({ narrow }: { narrow: boolean }) {
  const { status } = useAuth();
  return (
    <header className="mkt-hero">
      <div className="mkt-hero-field">
        <SwarmField density={narrow ? 46 : 84} />
      </div>
      <div className="mkt-container mkt-hero-inner">
        <div className="mkt-hero-chip mkt-fade">
          <MonoChip tone="ghost">
            <StatusDot tone="live" pulse size={6} /> coordination plane for
            your coding-agent swarm
          </MonoChip>
        </div>
        <h1 className="mkt-fade mkt-fade--1">
          Your agents work the night shift.
          <br />
          <span className="dim">You review at sunrise.</span>
        </h1>
        <p className="mkt-hero-tagline">
          NightSwarm is a coordination plane for swarms of your coding agents —
          Claude Code &amp; Codex. Enroll your own Macs, schedule overnight work,
          and reach every agent from anywhere through the Claude app.
        </p>
        <div id="get-started" className="mkt-hero-cta mkt-fade mkt-fade--3">
          {status === "loading" ? (
            <MonoChip tone="ghost">loading…</MonoChip>
          ) : status === "signed-in" ? (
            <SignedInCard />
          ) : (
            <MagicLinkCard />
          )}
        </div>
        <div className="mkt-hero-note">
          No password. No credit card. Installs as a menubar app.
        </div>
      </div>
      <div className="mkt-container mkt-two-wrap">
        <TwoSided />
      </div>
    </header>
  );
}

/* ── How it works ────────────────────────────────────────────────── */

const STEPS = [
  {
    num: "01",
    icon: I.cpu,
    t: "Enroll your Macs",
    b: "Install the menubar app on any Mac you own. It registers the machine, reports its capabilities, and keeps a quiet heartbeat.",
  },
  {
    num: "02",
    icon: I.bot,
    t: "Define agents & schedules",
    b: "Stand up persistent agents — research, vault, health — and give them cron-driven work. The humane editor writes the cron for you.",
  },
  {
    num: "03",
    icon: I.sun,
    t: "Wake to results",
    b: "Overnight runs stream to the dashboard and push when they land. Review the morning report; reach any agent from the Claude app.",
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="mkt-anchor">
      <div className="mkt-container mkt-sec-pad">
        <div className="mkt-eyebrow mkt-eyebrow--accent">How it works</div>
        <h2 className="mkt-h2 mkt-h2--narrow">Three moves to a working swarm.</h2>
        <div className="mkt-steps">
          {STEPS.map((s) => (
            <div className="mkt-step" key={s.num}>
              <div className="mkt-step-top">
                <span className="mkt-step-icon">
                  <Icon d={s.icon} size={18} />
                </span>
                <span className="mkt-step-num">{s.num}</span>
              </div>
              <h3>{s.t}</h3>
              <p>{s.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── From the Claude app ─────────────────────────────────────────── */

const MCP_TOOLS: Array<[string, string]> = [
  ["swarm_status", "Machines, sessions, and the job queue at a glance"],
  ["session_status", "Check a running session without leaving chat"],
  ["create_schedule", "Add overnight work in plain language"],
];

function MiniSwarmWidget() {
  return (
    <div className="mkt-widget">
      <div className="mkt-widget-head">
        <SwarmMoon size={16} />
        <span className="t">swarm status</span>
        <span className="right">
          <StatusPill status="online">all systems normal</StatusPill>
        </span>
      </div>
      <div className="mkt-widget-stats">
        {(
          [
            ["machines", "4", "online"],
            ["sessions", "1", "working"],
            ["queued", "3", "tonight"],
          ] as const
        ).map(([k, v, s]) => (
          <div className="mkt-widget-stat" key={k}>
            <div className="k">{k}</div>
            <div className="vrow">
              <span className="v">{v}</span>
              <span className="s">{s}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mkt-widget-live">
        <StatusDot tone="live" pulse size={6} />
        <span className="who">research</span>
        <span className="what">· Q3 brief · 6m12s</span>
      </div>
    </div>
  );
}

function FromClaude() {
  return (
    <section id="from-claude" className="mkt-fromclaude mkt-anchor">
      <div className="mkt-container mkt-sec-pad mkt-fc-grid">
        <div className="mkt-fc-copy">
          <div className="mkt-eyebrow mkt-eyebrow--accent">
            From the Claude app
          </div>
          <h2 className="mkt-h2">
            Reach the swarm from anywhere you already chat.
          </h2>
          <p className="mkt-sub">
            NightSwarm connects to Claude over MCP. Ask for status, kick off
            work, or check on a long-running job — tool responses render as
            live widgets right in the thread.
          </p>
          <div className="mkt-fc-tools">
            {MCP_TOOLS.map(([cmd, desc]) => (
              <div className="mkt-fc-tool" key={cmd}>
                <span className="cmd">{cmd}</span>
                <span className="desc">{desc}</span>
              </div>
            ))}
          </div>
          {/* Widget-gallery teaser (docs/widget-gallery-page.md §5). */}
          <p className="mkt-sub">
            <Link to="/widgets" className="mkt-fc-gallery">
              See all eight widgets live, with demo data →
            </Link>
          </p>
        </div>
        <div
          className="mkt-fc-demo"
          role="img"
          aria-label="Claude app conversation example with a live swarm-status widget"
        >
          <div className="mkt-fc-demo-head">
            <Icon d={I.dot} size={14} style={{ color: "var(--color-accent)" }} />
            <span className="app">Claude</span>
            <span className="right">
              <MonoChip tone="ghost">MCP · nightswarm</MonoChip>
            </span>
          </div>
          <div className="mkt-fc-demo-body">
            <div className="mkt-msg-user">How's the swarm doing?</div>
            <div className="mkt-msg-toolrow">
              <MonoChip tone="accent">
                <Icon d={I.wrench} size={11} /> swarm_status
              </MonoChip>
              <span className="ms">· 240ms</span>
            </div>
            <MiniSwarmWidget />
            <div className="mkt-msg-reply">
              All four machines are online. <span className="cmd">research</span>{" "}
              is mid-run on the Q3 brief — about 6 minutes in.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Dispatch profiles (model × effort × intensity) ──────────────── */

const PROFILE_ROWS: Array<{
  task: string;
  chips: [string, string, string];
  swarm?: boolean;
}> = [
  { task: "Rename a config key across the repo", chips: ["haiku", "low", "light"] },
  { task: "Add the export endpoint + tests", chips: ["sonnet", "medium", "standard"] },
  { task: "Debug the flaky auth test — prove the fix", chips: ["opus", "high", "deep"] },
  {
    task: "Audit the whole repo for race conditions",
    chips: ["opus", "max", "swarm"],
    swarm: true,
  },
];

function DispatchProfiles() {
  return (
    <section id="dispatch" className="mkt-anchor">
      <div className="mkt-container mkt-sec-pad mkt-fc-grid">
        <div className="mkt-fc-copy">
          <div className="mkt-eyebrow mkt-eyebrow--accent">
            Dispatch profiles
          </div>
          <h2 className="mkt-h2">Every task gets the model it deserves.</h2>
          <p className="mkt-sub">
            Each dispatch carries a profile — <strong>model × effort ×
            intensity</strong>. Claude picks it per task, so light chores run
            on haiku while opus at max effort is saved for the runs that earn
            it. Your seat's hours go where they matter.
          </p>
          <div className="mkt-dp-rows">
            {PROFILE_ROWS.map((r) => (
              <div
                className={"mkt-dp-row" + (r.swarm ? " swarm" : "")}
                key={r.task}
              >
                <span className="task">{r.task}</span>
                <span className="chips">
                  {r.chips.map((c) => (
                    <MonoChip key={c} tone={r.swarm ? "accent" : "ghost"}>
                      {c}
                    </MonoChip>
                  ))}
                </span>
              </div>
            ))}
          </div>
          <p className="mkt-sub mkt-dp-note">
            <strong>swarm</strong> turns one dispatch into a fleet: cheap
            finders fan out in parallel, skeptic agents try to refute every
            claim, and a completeness critic gates the finish.
          </p>
        </div>
        <div
          className="mkt-fc-demo"
          role="img"
          aria-label="Claude app conversation dispatching a swarm-intensity audit"
        >
          <div className="mkt-fc-demo-head">
            <Icon d={I.dot} size={14} style={{ color: "var(--color-accent)" }} />
            <span className="app">Claude</span>
            <span className="right">
              <MonoChip tone="ghost">MCP · nightswarm</MonoChip>
            </span>
          </div>
          <div className="mkt-fc-demo-body">
            <div className="mkt-msg-user">
              Audit roundtable for race conditions tonight. Be exhaustive.
            </div>
            <div className="mkt-msg-toolrow">
              <MonoChip tone="accent">
                <Icon d={I.wrench} size={11} /> run_job
              </MonoChip>
              <MonoChip tone="ghost">opus · max · swarm</MonoChip>
            </div>
            <div className="mkt-night-term">
              <div className="t-dim">02:00 · studio-mini</div>
              <div className="t-row">
                <span className="t-ok">›</span> audit woke up{" "}
                <span className="t-dim">[claude · opus · max · swarm]</span>
              </div>
              <div className="t-row">
                <span className="t-acc">⚙</span> fan-out · 6 finders on haiku
              </div>
              <div className="t-row">
                <span className="t-acc">⚙</span> judge panel ×3 · skeptics try
                to refute
              </div>
              <div className="t-row">
                <span className="t-acc">⚙</span> completeness critic · 2 gaps →
                re-swept
              </div>
              <div className="t-ok">
                ✓ 14 findings · 11 survived review · delivered
              </div>
            </div>
            <div className="mkt-msg-reply">
              Queued for tonight at <span className="cmd">swarm</span>{" "}
              intensity — haiku finders sweep in parallel; opus verifies every
              claim before it reaches your morning report.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── The night shift (terminal report) ───────────────────────────── */

const REPORT_ROWS: Array<[string, string, string, string, string]> = [
  ["02:00", "research", "Q3 competitive brief", "delivered", "6m12s"],
  ["03:10", "refactor", "Codex: migrate api to v2 client", "delivered", "4m38s"],
  ["03:30", "vault", "Nightly encrypted backup → S3", "delivered", "2m04s"],
  ["04:15", "health", "Sync Whoop + Oura, weekly rollup", "delivered", "48s"],
  ["05:00", "research", "Inbox triage, draft 3 replies", "failed", "—"],
];

function NightShift() {
  return (
    <section id="night-shift" className="mkt-night mkt-anchor">
      <div className="mkt-night-bg mkt-dotgrid" />
      <div className="mkt-container mkt-sec-pad">
        <div className="mkt-night-head">
          <div className="mkt-eyebrow mkt-eyebrow--accent">The night shift</div>
          <h2 className="mkt-h2">You wake up to a report, not a backlog.</h2>
          <p className="mkt-sub">
            Every scheduled run is captured end to end. Sunrise brings one
            summary — what ran, what shipped, and what needs you.
          </p>
        </div>
        <div className="mkt-term" role="img" aria-label="Morning report terminal example">
          <div className="mkt-term-bar">
            <span className="mkt-term-dots">
              <span />
              <span />
              <span />
            </span>
            <span className="mkt-term-path">~/nightswarm · morning report</span>
            <span className="mkt-term-time">06:42</span>
          </div>
          <div className="mkt-term-body">
            <div className="mkt-term-cmd">
              <span className="dol">$</span> nightswarm report
            </div>
            <div className="mkt-term-sum">
              <span className="ok">4 delivered</span> ·{" "}
              <span className="bad">1 failed</span> · 4 online
            </div>
            <div className="mkt-report-rows">
              {REPORT_ROWS.map(([time, agent, task, st, dur]) => (
                <div className="mkt-report-row" key={time}>
                  <span className="rt">{time}</span>
                  <span className="ragent">{agent}</span>
                  <span className="rtask">{task}</span>
                  <span className="rpill">
                    <StatusPill status={st} />
                  </span>
                  <span className="rdur">{dur}</span>
                </div>
              ))}
            </div>
            <div className="mkt-term-note">
              <span className="warn">!</span> inbox-triage rate-limited.{" "}
              <span className="acc">retry 06:45</span>
              <span className="mkt-term-cursor" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Capabilities ────────────────────────────────────────────────── */

const CAPS = [
  {
    icon: I.cpu,
    t: "Your own hardware & seats",
    b: "Agents run on Macs you own — Claude Code and Codex side by side, each on the subscription seat you already pay for. No rented cloud box, no metered inference, no data leaving your machines unless you say so.",
  },
  {
    icon: I.zap,
    t: "Right-sized dispatches",
    b: "Every run carries a profile — model × effort × intensity. Haiku on light for chores, opus at max for swarm-intensity audits with adversarial verification built in.",
  },
  {
    icon: I.clock,
    t: "Cron, made humane",
    b: "Describe a schedule in plain language. NightSwarm round-trips it to a real cron string you can still hand-edit.",
  },
  {
    icon: I.signal,
    t: "Live session streams",
    b: "Watch any run as it happens — tool calls, thinking, and output — or replay the full transcript later.",
  },
  {
    icon: I.layers,
    t: "A real job queue",
    b: "Overnight work is queued, retried, and traced. Run-now when you can't wait; everything lands in the morning report.",
  },
  {
    icon: I.bell,
    t: "Push when it matters",
    b: "Install the dashboard as a PWA and get a push the moment a job delivers — or the moment one needs you.",
  },
  {
    icon: I.terminal,
    t: "MCP-native",
    b: "Every capability is a tool. Drive the whole swarm from the Claude app, with widgets that render inline.",
  },
  {
    icon: I.plus,
    t: "Capture, even offline",
    b: "Drop a thought from the Claude app and it lands in an inbox instantly — even with every machine asleep. Your notes agent files it on the next run, so nothing slips.",
  },
  {
    icon: I.calendar,
    t: "Follow-ups that come back",
    b: "Ask an agent to circle back — 'check Thursday whether she replied.' It fires once, at the moment you name, then clears itself away.",
  },
  {
    icon: I.moon,
    t: "Quiet hours, respected",
    b: "Overnight runs don't buzz you all night. Routine done-pings wait for morning; only the jobs that truly need you break through.",
  },
];

function Capabilities() {
  return (
    <section id="capabilities" className="mkt-caps mkt-anchor">
      <div className="mkt-container mkt-sec-pad">
        <div className="mkt-eyebrow mkt-eyebrow--accent">Capabilities</div>
        <h2 className="mkt-h2 mkt-h2--narrow">
          Everything a swarm needs to run unattended.
        </h2>
        <div className="mkt-caps-grid">
          {CAPS.map((c) => (
            <div className="mkt-cap" key={c.t}>
              <span className="ic">
                <Icon d={c.icon} size={20} />
              </span>
              <h3>{c.t}</h3>
              <p>{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── For teams & orgs ────────────────────────────────────────────── */

const TEAMS = [
  {
    icon: I.layers,
    t: "One swarm, every Mac",
    b: "Pool the team's machines into a single swarm. A job runs on whichever Mac is free — nobody waits on someone else's laptop, and capacity grows as the team does.",
  },
  {
    icon: I.list,
    t: "A backlog the swarm drains",
    b: "Anyone drops work onto a shared goal list from the Claude app. Overnight the machines claim tasks atomically — two never grab the same one — and drain it while you sleep.",
  },
  {
    icon: I.bell,
    t: "Everyone sees the night's work",
    b: "Each person gets their own push and a shared morning report. What ran, what shipped, and what needs a human is legible to the whole team — not just whoever kicked it off.",
  },
  {
    icon: I.gear,
    t: "Your code never leaves",
    b: "The plane routes and schedules; it never holds a repo token, an SSH key, or a line of your source. Every machine connects outbound-only, so secrets stay on hardware you control.",
  },
];

function ForTeams() {
  return (
    <section id="for-teams" className="mkt-anchor">
      <div className="mkt-container mkt-sec-pad">
        <div className="mkt-eyebrow mkt-eyebrow--accent">For teams &amp; orgs</div>
        <h2 className="mkt-h2 mkt-h2--narrow">
          Run it as a team, not a pile of laptops.
        </h2>
        <p className="mkt-sub">
          Invite your teammates and their Macs join one shared swarm. Work is
          pooled, claimed without collisions, and reported to everyone — and how
          much the swarm does on its own is a policy you set together, one rung
          at a time.
        </p>
        <div className="mkt-caps-grid">
          {TEAMS.map((c) => (
            <div className="mkt-cap" key={c.t}>
              <span className="ic">
                <Icon d={c.icon} size={20} />
              </span>
              <h3>{c.t}</h3>
              <p>{c.b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Autonomy ladder ─────────────────────────────────────────────── */

const RUNGS = [
  { r: "R0", t: "Observe", b: "Agents report state. Nothing runs without you.", pct: 8, here: false },
  { r: "R1", t: "Suggest", b: "Agents draft plans and queue work for your approval.", pct: 26, here: false },
  { r: "R2", t: "Run on schedule", b: "Approved jobs run on cron. You review the morning report.", pct: 52, here: true },
  { r: "R3", t: "Act on triggers", b: "Agents respond to events and chain follow-up work.", pct: 76, here: false },
  { r: "R4", t: "Self-direct", b: "Agents set their own overnight agenda within guardrails.", pct: 96, here: false },
];

function AutonomyLadder() {
  return (
    <section id="autonomy" className="mkt-ladder-sec mkt-anchor">
      <div className="mkt-container mkt-sec-pad">
        <div className="mkt-ladder-head">
          <div>
            <div className="mkt-eyebrow mkt-eyebrow--accent">Autonomy ladder</div>
            <h2 className="mkt-h2">
              You decide how much rope the swarm gets.
            </h2>
          </div>
          <MonoChip tone="accent">
            <StatusDot tone="live" size={6} /> you're at R2
          </MonoChip>
        </div>
        <div className="mkt-ladder">
          {RUNGS.map((r) => (
            <div className={"mkt-rung" + (r.here ? " here" : "")} key={r.r}>
              <span className="mkt-rung-id">{r.r}</span>
              <span className="mkt-rung-title">
                {r.t}
                {r.here && <span className="mkt-rung-you">● you</span>}
              </span>
              <span className="mkt-rung-body">{r.b}</span>
              <span className="mkt-rung-bar">
                <i style={{ width: `${r.pct}%` }} />
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Closing CTA ─────────────────────────────────────────────────── */

function ClosingCTA({ narrow }: { narrow: boolean }) {
  return (
    <section className="mkt-cta">
      <div className="mkt-cta-field">
        <SwarmField density={narrow ? 32 : 56} />
      </div>
      <div className="mkt-container mkt-cta-inner">
        <span className="mkt-cta-moon">
          <SwarmMoon size={64} animate />
        </span>
        <h2>Hand the night shift to your swarm.</h2>
        <p>
          Enroll your first Mac in under two minutes. Review your first
          overnight run tomorrow.
        </p>
        <div className="mkt-cta-btns">
          <a className="ns-btn ns-btn--primary ns-btn--lg" href="#get-started">
            Get started
          </a>
          <a className="ns-btn ns-btn--outline ns-btn--lg" href="#how-it-works">
            See how it works
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── Footer (keeps MartzmakesWordmark usage + legal routes) ──────── */

function MktFooter() {
  return (
    <footer className="mkt-footer">
      <div className="mkt-container mkt-footer-inner">
        <div className="mkt-footer-grid">
          <div className="mkt-footer-brand">
            <NSWordmark size={17} />
            <p>
              A coordination plane for your coding-agent swarm — Claude Code
              &amp; Codex. Your hardware, your agents, your night shift.
            </p>
            <div className="chiprow">
              <MonoChip tone="ghost">nightswarm.app · beta</MonoChip>
            </div>
          </div>
          <div className="mkt-footer-col">
            <div className="mkt-footer-h">Product</div>
            <nav aria-label="Product">
              <a href="#how-it-works">How it works</a>
              <a href="#from-claude">From Claude</a>
              <a href="#dispatch">Dispatch profiles</a>
              <a href="#night-shift">Night shift</a>
              <a href="#capabilities">Capabilities</a>
              <Link to="/widgets">Widget gallery</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/docs">Docs</Link>
            </nav>
          </div>
          <div className="mkt-footer-col">
            <div className="mkt-footer-h">Account</div>
            <nav aria-label="Account">
              <a href="#get-started">Sign in</a>
              <Link to="/dashboard">Dashboard</Link>
            </nav>
          </div>
          <div className="mkt-footer-col">
            <div className="mkt-footer-h">Company</div>
            <nav aria-label="Company">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <a href="mailto:matt@martzmakes.com">Contact</a>
            </nav>
          </div>
        </div>
        <div className="mkt-footer-bar">
          <span className="mkt-footer-copy">
            © 2026 nightswarm · runs after dark
          </span>
          <span className="mkt-footer-built">
            <span>built by</span>
            <MartzmakesWordmarkLink size={12.5} />
          </span>
        </div>
      </div>
    </footer>
  );
}

/* ── Stripe checkout return banner (phase 10 §C) ─────────────────── */

function PurchaseBanner() {
  const { purchase, dismiss } = usePurchaseStatus();
  if (!purchase) return null;
  const success = purchase === "success";
  return (
    <div className={`mkt-purchase ${purchase}`} role="status">
      <span className="ic">
        <Icon d={success ? I.check : I.x} size={14} />
      </span>
      <span>
        {success
          ? "Payment complete — your plan is active."
          : "Checkout cancelled — nothing was charged."}
      </span>
      <Link to="/pricing">View plans</Link>
      <button type="button" className="x" onClick={dismiss} aria-label="Dismiss">
        <Icon d={I.x} size={13} />
      </button>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */

export default function Landing() {
  const narrow = useNarrow();
  return (
    <>
      <MktNav />
      <PurchaseBanner />
      <MktHero narrow={narrow} />
      <HowItWorks />
      <FromClaude />
      <DispatchProfiles />
      <NightShift />
      <Capabilities />
      <ForTeams />
      <AutonomyLadder />
      <ClosingCTA narrow={narrow} />
      <MktFooter />
    </>
  );
}
